/**
 * InsurancePolicyModal — Create / Edit insurance policy modal
 *
 * Handles both:
 *  - Create mode: "Add Policy" → new policy creation
 *  - Edit mode: Pre-populates all fields with existing policy data
 *
 * Schema fields (from schema-compliance.ts / compliance-insurance.ts):
 *  - insuranceType: pi | public_liability | cyber | management_liability | other
 *  - policyStatus: active | expired | cancelled | pending_renewal (NO 'renewed')
 *  - coverAmount (NOT coverageAmount)
 *  - renewalReminderDate (NOT renewalDate)
 *  - NO AFG fields, NO brokerContact/brokerEmail/brokerPhone, NO exclusionsSummary
 *  - HAS: premiumFrequency, policyDocumentUrl, certificateUrl, coverageSummary
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InsurancePolicyData {
  id?: string;
  insuranceType?: string;
  policyNumber?: string | null;
  policyName?: string;
  insurerName?: string;
  brokerName?: string | null;
  coverAmount?: string | null;
  excessAmount?: string | null;
  coverageSummary?: string | null;
  inceptionDate?: string | null;
  expiryDate?: string | null;
  renewalReminderDate?: string | null;
  policyStatus?: string;
  premiumAmount?: string | null;
  premiumFrequency?: string | null;
  policyDocumentUrl?: string | null;
  certificateUrl?: string | null;
}

interface InsurancePolicyModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingPolicy?: InsurancePolicyData | null;
  onSuccess?: (policyId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INSURANCE_TYPES = [
  { value: "pi", label: "Professional Indemnity" },
  { value: "public_liability", label: "Public Liability" },
  { value: "cyber", label: "Cyber" },
  { value: "management_liability", label: "Management Liability" },
  { value: "other", label: "Other" },
];

const POLICY_STATUSES = [
  { value: "active", label: "Active" },
  { value: "pending_renewal", label: "Pending Renewal" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const PREMIUM_FREQUENCIES = [
  { value: "annual", label: "Annual" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InsurancePolicyModal({
  open,
  onOpenChange,
  editingPolicy,
  onSuccess,
}: InsurancePolicyModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingPolicy?.id;

  // Form state
  const [insuranceType, setInsuranceType] = useState("");
  const [policyName, setPolicyName] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [insurerName, setInsurerName] = useState("");
  const [brokerName, setBrokerName] = useState("");
  const [policyStatus, setPolicyStatus] = useState("active");
  const [inceptionDate, setInceptionDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [renewalReminderDate, setRenewalReminderDate] = useState("");
  const [premiumAmount, setPremiumAmount] = useState("");
  const [premiumFrequency, setPremiumFrequency] = useState("annual");
  const [coverAmount, setCoverAmount] = useState("");
  const [excessAmount, setExcessAmount] = useState("");
  const [coverageSummary, setCoverageSummary] = useState("");
  const [policyDocumentUrl, setPolicyDocumentUrl] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");

  // Reset / pre-populate form when modal opens or editingPolicy changes
  useEffect(() => {
    if (open && editingPolicy) {
      setInsuranceType(editingPolicy.insuranceType ?? "");
      setPolicyName(editingPolicy.policyName ?? "");
      setPolicyNumber(editingPolicy.policyNumber ?? "");
      setInsurerName(editingPolicy.insurerName ?? "");
      setBrokerName(editingPolicy.brokerName ?? "");
      setPolicyStatus(editingPolicy.policyStatus ?? "active");
      setInceptionDate(editingPolicy.inceptionDate ?? "");
      setExpiryDate(editingPolicy.expiryDate ?? "");
      setRenewalReminderDate(editingPolicy.renewalReminderDate ?? "");
      setPremiumAmount(editingPolicy.premiumAmount ?? "");
      setPremiumFrequency(editingPolicy.premiumFrequency ?? "annual");
      setCoverAmount(editingPolicy.coverAmount ?? "");
      setExcessAmount(editingPolicy.excessAmount ?? "");
      setCoverageSummary(editingPolicy.coverageSummary ?? "");
      setPolicyDocumentUrl(editingPolicy.policyDocumentUrl ?? "");
      setCertificateUrl(editingPolicy.certificateUrl ?? "");
    } else if (open && !editingPolicy) {
      // Reset for create mode
      setInsuranceType("");
      setPolicyName("");
      setPolicyNumber("");
      setInsurerName("");
      setBrokerName("");
      setPolicyStatus("active");
      setInceptionDate("");
      setExpiryDate("");
      setRenewalReminderDate("");
      setPremiumAmount("");
      setPremiumFrequency("annual");
      setCoverAmount("");
      setExcessAmount("");
      setCoverageSummary("");
      setPolicyDocumentUrl("");
      setCertificateUrl("");
    }
  }, [open, editingPolicy]);

  const isValid =
    insuranceType.length > 0 &&
    policyName.trim().length > 0 &&
    insurerName.trim().length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/insurance", {
        insuranceType,
        policyName: policyName.trim(),
        policyNumber: policyNumber.trim() || undefined,
        insurerName: insurerName.trim(),
        brokerName: brokerName.trim() || undefined,
        policyStatus,
        inceptionDate: inceptionDate || undefined,
        expiryDate: expiryDate || undefined,
        renewalReminderDate: renewalReminderDate || undefined,
        premiumAmount: premiumAmount || undefined,
        premiumFrequency: premiumFrequency || undefined,
        coverAmount: coverAmount || undefined,
        excessAmount: excessAmount || undefined,
        coverageSummary: coverageSummary.trim() || undefined,
        policyDocumentUrl: policyDocumentUrl.trim() || undefined,
        certificateUrl: certificateUrl.trim() || undefined,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to create policy");
      }
      return response.json();
    },
    onSuccess: (data: InsurancePolicyData) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/insurance"] });
      toast({ title: "Insurance policy created successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id ?? "");
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingPolicy?.id) throw new Error("No policy ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/insurance/${editingPolicy.id}`,
        {
          insuranceType,
          policyName: policyName.trim(),
          policyNumber: policyNumber.trim() || undefined,
          insurerName: insurerName.trim(),
          brokerName: brokerName.trim() || undefined,
          policyStatus,
          inceptionDate: inceptionDate || undefined,
          expiryDate: expiryDate || undefined,
          renewalReminderDate: renewalReminderDate || undefined,
          premiumAmount: premiumAmount || undefined,
          premiumFrequency: premiumFrequency || undefined,
          coverAmount: coverAmount || undefined,
          excessAmount: excessAmount || undefined,
          coverageSummary: coverageSummary.trim() || undefined,
          policyDocumentUrl: policyDocumentUrl.trim() || undefined,
          certificateUrl: certificateUrl.trim() || undefined,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error ?? "Failed to update policy");
      }
      return response.json();
    },
    onSuccess: (data: InsurancePolicyData) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/insurance"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/insurance/${data.id}`],
      });
      toast({ title: "Insurance policy updated successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id ?? "");
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-insurance-policy-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Policy" : "Add Insurance Policy"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the insurance policy details below."
              : "Add a new insurance policy to the registry."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Policy Details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Policy Details
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Insurance Type */}
              <div className="space-y-1.5">
                <Label>
                  Insurance Type <span className="text-destructive">*</span>
                </Label>
                <Select value={insuranceType} onValueChange={setInsuranceType}>
                  <SelectTrigger data-testid="select-insurance-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSURANCE_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Policy Status */}
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={policyStatus} onValueChange={setPolicyStatus}>
                  <SelectTrigger data-testid="select-policy-status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {POLICY_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Policy Name */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="policy-name">
                  Policy Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="policy-name"
                  data-testid="input-policy-name"
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                  placeholder="e.g. Professional Indemnity 2026"
                />
              </div>

              {/* Policy Number */}
              <div className="space-y-1.5">
                <Label htmlFor="policy-number">Policy Number</Label>
                <Input
                  id="policy-number"
                  data-testid="input-policy-number"
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                  placeholder="e.g. PI-2026-001"
                />
              </div>

              {/* Insurer Name */}
              <div className="space-y-1.5">
                <Label htmlFor="insurer-name">
                  Insurer <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="insurer-name"
                  data-testid="input-insurer-name"
                  value={insurerName}
                  onChange={(e) => setInsurerName(e.target.value)}
                  placeholder="e.g. QBE Insurance"
                />
              </div>

              {/* Broker Name */}
              <div className="space-y-1.5">
                <Label htmlFor="broker-name">Broker</Label>
                <Input
                  id="broker-name"
                  data-testid="input-broker-name"
                  value={brokerName}
                  onChange={(e) => setBrokerName(e.target.value)}
                  placeholder="e.g. Marsh Insurance Brokers"
                />
              </div>

              {/* Inception Date */}
              <div className="space-y-1.5">
                <Label htmlFor="inception-date">Inception Date</Label>
                <Input
                  id="inception-date"
                  data-testid="input-inception-date"
                  type="date"
                  value={inceptionDate}
                  onChange={(e) => setInceptionDate(e.target.value)}
                />
              </div>

              {/* Expiry Date */}
              <div className="space-y-1.5">
                <Label htmlFor="expiry-date">Expiry Date</Label>
                <Input
                  id="expiry-date"
                  data-testid="input-expiry-date"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>

              {/* Renewal Reminder Date */}
              <div className="space-y-1.5">
                <Label htmlFor="renewal-reminder-date">Renewal Reminder Date</Label>
                <Input
                  id="renewal-reminder-date"
                  data-testid="input-renewal-reminder-date"
                  type="date"
                  value={renewalReminderDate}
                  onChange={(e) => setRenewalReminderDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Coverage */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Coverage
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Premium Amount */}
              <div className="space-y-1.5">
                <Label htmlFor="premium-amount">Premium Amount</Label>
                <Input
                  id="premium-amount"
                  data-testid="input-premium-amount"
                  value={premiumAmount}
                  onChange={(e) => setPremiumAmount(e.target.value)}
                  placeholder="e.g. 5000.00"
                />
              </div>

              {/* Premium Frequency */}
              <div className="space-y-1.5">
                <Label>Premium Frequency</Label>
                <Select value={premiumFrequency} onValueChange={setPremiumFrequency}>
                  <SelectTrigger data-testid="select-premium-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREMIUM_FREQUENCIES.map((f) => (
                      <SelectItem key={f.value} value={f.value}>
                        {f.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Cover Amount */}
              <div className="space-y-1.5">
                <Label htmlFor="cover-amount">Cover Amount</Label>
                <Input
                  id="cover-amount"
                  data-testid="input-cover-amount"
                  value={coverAmount}
                  onChange={(e) => setCoverAmount(e.target.value)}
                  placeholder="e.g. 1000000.00"
                />
              </div>

              {/* Excess Amount */}
              <div className="space-y-1.5">
                <Label htmlFor="excess-amount">Excess Amount</Label>
                <Input
                  id="excess-amount"
                  data-testid="input-excess-amount"
                  value={excessAmount}
                  onChange={(e) => setExcessAmount(e.target.value)}
                  placeholder="e.g. 2500.00"
                />
              </div>

              {/* Coverage Summary */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="coverage-summary">Coverage Summary</Label>
                <Textarea
                  id="coverage-summary"
                  data-testid="input-coverage-summary"
                  value={coverageSummary}
                  onChange={(e) => setCoverageSummary(e.target.value)}
                  placeholder="Summary of coverage included..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Documents
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Policy Document URL */}
              <div className="space-y-1.5">
                <Label htmlFor="policy-document-url">Policy Document URL</Label>
                <Input
                  id="policy-document-url"
                  data-testid="input-policy-document-url"
                  value={policyDocumentUrl}
                  onChange={(e) => setPolicyDocumentUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              {/* Certificate URL */}
              <div className="space-y-1.5">
                <Label htmlFor="certificate-url">Certificate URL</Label>
                <Input
                  id="certificate-url"
                  data-testid="input-certificate-url"
                  value={certificateUrl}
                  onChange={(e) => setCertificateUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save-policy"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Add Policy"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
